import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  TextField,
  TableSortLabel,
  TablePagination,
  useTheme,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import {
  useGetBillingCycleList,
  useCreateBillingCycle,
} from "../../hooks/api/useBillingCycle";
import type { BillingCycle } from "../../Graphql/types/billingCycle";
import {
  formatDurationUnitLabel,
  DURATION_UNITS,
  type DurationUnit,
} from "../../Graphql/types/billingCycle";
import { getBillingCycleDetailPath } from "../../constants/routes";
import { adminSurface, adminTableRowSx } from "../../styles/adminUiTokens";
import MembershipColumnFilterField from "./MembershipColumnFilterField";
import {
  compareSortValues,
  hasActiveFilters,
  matchesStatusFilter,
  matchesTextFilter,
  type SortOrder,
} from "./utils/membershipTableUtils";

type OrderBy = "code" | "label" | "duration" | "active";
type Order = SortOrder;

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

type BillingCycleFieldFilters = {
  code: string;
  label: string;
  duration: string;
  status: string;
};

const EMPTY_BILLING_CYCLE_FILTERS: BillingCycleFieldFilters = {
  code: "",
  label: "",
  duration: "",
  status: "",
};

function formatDuration(bc: BillingCycle): string {
  return `${bc.durationValue} ${formatDurationUnitLabel(bc.durationUnit).toLowerCase()}`;
}

function getSortValue(bc: BillingCycle, field: OrderBy): string | number {
  if (field === "code") return bc.code;
  if (field === "label") return bc.label;
  if (field === "duration") return `${bc.durationValue}-${bc.durationUnit}`;
  return bc.isActive ? 1 : 0;
}

function filterAndSort(
  list: BillingCycle[],
  filters: BillingCycleFieldFilters,
  orderBy: OrderBy,
  order: Order,
): BillingCycle[] {
  const filtered = list.filter(
    (bc) =>
      matchesTextFilter(bc.code, filters.code) &&
      matchesTextFilter(bc.label, filters.label) &&
      matchesTextFilter(formatDuration(bc), filters.duration) &&
      matchesStatusFilter(bc.isActive, filters.status),
  );
  return filtered.sort((a, b) =>
    compareSortValues(
      getSortValue(a, orderBy),
      getSortValue(b, orderBy),
      order,
    ),
  );
}

const BillingCycleListTable: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [cycles, setCycles] = useState<BillingCycle[]>([]);
  const [fieldFilters, setFieldFilters] = useState<BillingCycleFieldFilters>(
    EMPTY_BILLING_CYCLE_FILTERS,
  );
  const [orderBy, setOrderBy] = useState<OrderBy>("code");
  const [order, setOrder] = useState<Order>("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addCode, setAddCode] = useState("");
  const [addLabel, setAddLabel] = useState("");
  const [addDurationValue, setAddDurationValue] = useState(1);
  const [addDurationUnit, setAddDurationUnit] = useState<DurationUnit>("MONTH");

  const getBillingCycleList = useGetBillingCycleList();
  const createBillingCycle = useCreateBillingCycle();

  useEffect(() => {
    getBillingCycleList
      .mutateAsync({ activeOnly: false })
      .then((res) => {
        setCycles(res.listBillingCycles?.billingCycleList ?? []);
      })
      .catch(() => {});
  }, []);

  const handleAddSuccess = () => {
    getBillingCycleList
      .mutateAsync({ activeOnly: false })
      .then((res) => setCycles(res.listBillingCycles?.billingCycleList ?? []))
      .catch(() => {});
    setAddModalOpen(false);
    setAddCode("");
    setAddLabel("");
    setAddDurationValue(1);
    setAddDurationUnit("MONTH");
  };

  const handleCreateBillingCycle = () => {
    if (!addCode.trim() || !addLabel.trim()) return;
    createBillingCycle
      .mutateAsync({
        code: addCode.trim(),
        label: addLabel.trim(),
        durationValue: addDurationValue,
        durationUnit: addDurationUnit,
      })
      .then(() => handleAddSuccess())
      .catch(() => {});
  };

  const sorted = useMemo(
    () => filterAndSort(cycles, fieldFilters, orderBy, order),
    [cycles, fieldFilters, orderBy, order],
  );

  const updateFieldFilter = (
    key: keyof BillingCycleFieldFilters,
    value: string,
  ) => {
    setFieldFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleSort = (field: OrderBy) => {
    const isAsc = orderBy === field && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(field);
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const isLoading = getBillingCycleList.isPending && cycles.length === 0;
  const isEmpty = !isLoading && cycles.length === 0;
  const noSearchResults = !isEmpty && sorted.length === 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper elevation={0} sx={{ ...adminSurface, width: "100%" }}>
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {hasActiveFilters(fieldFilters) && (
            <Button
              size="small"
              color="inherit"
              onClick={() => setFieldFilters(EMPTY_BILLING_CYCLE_FILTERS)}
            >
              Clear column filters
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            size="medium"
            startIcon={<Add />}
            onClick={() => setAddModalOpen(true)}
          >
            Add
          </Button>
        </Box>
        <Dialog
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Add billing cycle</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
              <TextField
                label="Code"
                value={addCode}
                onChange={(e) => setAddCode(e.target.value)}
                fullWidth
                size="small"
                required
                placeholder="e.g. SIX_MONTH"
              />
              <TextField
                label="Label"
                value={addLabel}
                onChange={(e) => setAddLabel(e.target.value)}
                fullWidth
                size="small"
                required
                placeholder="e.g. Six month"
              />
              <TextField
                label="Duration value"
                type="number"
                inputProps={{ min: 1 }}
                value={addDurationValue}
                onChange={(e) =>
                  setAddDurationValue(parseInt(e.target.value, 10) || 1)
                }
                fullWidth
                size="small"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Duration unit</InputLabel>
                <Select
                  value={addDurationUnit}
                  label="Duration unit"
                  onChange={(e) =>
                    setAddDurationUnit(e.target.value as DurationUnit)
                  }
                >
                  {DURATION_UNITS.map((u) => (
                    <MenuItem key={u} value={u}>
                      {formatDurationUnitLabel(u)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setAddModalOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateBillingCycle}
              disabled={
                !addCode.trim() ||
                !addLabel.trim() ||
                createBillingCycle.isPending
              }
            >
              {createBillingCycle.isPending ? "Creating…" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor:
                    theme.palette.mode === "light" ? "grey.50" : "grey.900",
                }}
              >
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "code"}
                    direction={orderBy === "code" ? order : "asc"}
                    onClick={() => handleSort("code")}
                  >
                    Code
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "label"}
                    direction={orderBy === "label" ? order : "asc"}
                    onClick={() => handleSort("label")}
                  >
                    Label
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "duration"}
                    direction={orderBy === "duration" ? order : "asc"}
                    onClick={() => handleSort("duration")}
                  >
                    Duration
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "active"}
                    direction={orderBy === "active" ? order : "asc"}
                    onClick={() => handleSort("active")}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 48, py: 2 }} />
              </TableRow>
              <TableRow
                sx={{
                  bgcolor:
                    theme.palette.mode === "light" ? "grey.100" : "grey.800",
                }}
              >
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.code}
                    onChange={(v) => updateFieldFilter("code", v)}
                    aria-label="Filter by code"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.label}
                    onChange={(v) => updateFieldFilter("label", v)}
                    aria-label="Filter by label"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.duration}
                    onChange={(v) => updateFieldFilter("duration", v)}
                    aria-label="Filter by duration"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.status}
                    onChange={(v) => updateFieldFilter("status", v)}
                    placeholder="Active…"
                    aria-label="Filter by status"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton variant="text" width="40%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="50%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                ))
              ) : isEmpty ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 6, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      No pricings (billing cycles) found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : noSearchResults ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 6, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      No pricings match the current column filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((bc, index) => (
                  <TableRow
                    key={bc.billingCycleId}
                    hover
                    sx={{
                      ...adminTableRowSx(index),
                      cursor: "pointer",
                      "&:hover .row-arrow": { opacity: 1 },
                    }}
                    onClick={() =>
                      navigate(getBillingCycleDetailPath(bc.billingCycleId))
                    }
                  >
                    <TableCell
                      sx={{
                        py: 2,
                        fontFamily: "monospace",
                        fontSize: "0.875rem",
                      }}
                    >
                      {bc.code}
                    </TableCell>
                    <TableCell sx={{ py: 2, fontWeight: 500 }}>
                      {bc.label}
                    </TableCell>
                    <TableCell sx={{ py: 2, color: "text.secondary" }}>
                      {formatDuration(bc)}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={bc.isActive ? "Active" : "Inactive"}
                        size="small"
                        color={bc.isActive ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, width: 48 }}>
                      <ArrowForwardIos
                        className="row-arrow"
                        sx={{
                          fontSize: 14,
                          color: "primary.main",
                          opacity: 0,
                          transition: "opacity 0.2s ease",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!isLoading && !isEmpty && sorted.length > 0 && (
          <TablePagination
            component="div"
            count={sorted.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Rows:"
            sx={{ borderTop: 1, borderColor: "divider" }}
          />
        )}
      </Paper>
    </Box>
  );
};

export default BillingCycleListTable;
